import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { can as canGrant, allowedTeamIds, type Permission, type RoleGrant } from "@guestpilot/contracts";
import { useGet } from "./api/hooks";
import type { Me, TenantState } from "./api/types";
import { useT } from "./i18n";
import type { TeamsInfo } from "./teams";

export interface Session {
  me: Me;
  grants: RoleGrant[];
  teams: TeamsInfo;
  tenantState: TenantState;
  /** write actions are blocked while the account is read-only, consent was lost, or staff suspended it (BR-016, BR-252) */
  readOnly: boolean;
  isPrimaryAdmin: boolean;
  can: (perm: Permission, teamId?: string) => boolean;
  /** `null` when allowed, else the reason shown as a tooltip on the disabled control (04 §7) */
  gate: (perm: Permission, teamId?: string) => string | undefined;
  /** Teams where the person has a team-scoped role, or `"all"` */
  scope: (perm: Permission) => "all" | string[];
  isTeamScopedOnly: boolean;
  /** reloads `/me` (account state, roles) after an action that may change them */
  refresh: () => Promise<void>;
}

const Ctx = createContext<Session | null>(null);

const READ_ONLY_STATES: TenantState[] = ["read_only", "consent_lost"];

export function buildSession(
  me: Me,
  teams: TeamsInfo,
  t: (k: string) => string,
  refresh: () => Promise<void> = async () => undefined,
): Session {
  const grants: RoleGrant[] = me.roles.flatMap((r) =>
    r.role ? [{ role: r.role, ...(r.teamId ? { teamId: r.teamId } : {}) } as RoleGrant] : [],
  );
  const readOnly =
    Boolean(me.tenant.readOnly) ||
    READ_ONLY_STATES.includes(me.tenant.state) ||
    me.tenant.suspension === "suspended";
  // Teams mobile shows no prices, plans or upgrade prompts: the subscription permissions are not offered there, so the menu entry,
  // the page and every upgrade button disappear with them
  const can = (perm: Permission, teamId?: string) =>
    !(teams.mobile && (perm === "SUBSCRIPTION_READ" || perm === "SUBSCRIPTION_MANAGE")) &&
    canGrant(grants, perm, teamId ? { teamId } : {});
  return {
    me,
    grants,
    teams,
    tenantState: me.tenant.state,
    readOnly,
    isPrimaryAdmin: grants.some((g) => g.role === "primary_admin"),
    can,
    gate: (perm, teamId) =>
      !can(perm, teamId) ? t("ui:gate.noPermission") : readOnly ? t("ui:gate.readOnly") : undefined,
    scope: (perm) => allowedTeamIds(grants, perm),
    refresh,
    isTeamScopedOnly: grants.length > 0 && grants.every((g) => g.role === "owner" || g.role === "delegate"),
  };
}

/** Loads `/me` once; permissions here are UX only, the API enforces them (BR-007). */
export function SessionProvider({
  teams,
  children,
}: {
  teams: TeamsInfo;
  children: (state: { session: Session } | { loading: true } | { error: Error }) => ReactNode;
}) {
  const t = useT();
  const q = useGet<Me>("/api/v1/me", undefined, { staleTime: 60_000 });
  const qc = useQueryClient();
  const session = useMemo(
    () =>
      q.data
        ? buildSession(q.data, teams, t, () => qc.invalidateQueries({ queryKey: ["/api/v1/me"] }))
        : null,
    [q.data, teams, t, qc],
  );
  return (
    <Ctx.Provider value={session}>
      {session ? children({ session }) : q.error ? children({ error: q.error }) : children({ loading: true })}
    </Ctx.Provider>
  );
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error("No session: render inside SessionProvider once /me has loaded");
  return s;
}

export const SessionContext = Ctx;
